import React, { useState } from 'react';
import { X, Wand2, Copy, Save, Loader2, Edit3 } from 'lucide-react';
import BoxLoader from '../ui/box-loader';
import { openAIService } from '../../services/openai';
import { Prompt } from '../../types';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useScrollLock } from '../../hooks/useScrollLock';

interface PromptVariationModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalPrompt: Prompt;
  onSaveVariation: (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void;
}

interface VariationNamingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  defaultName: string;
  variationIndex: number;
  totalVariations: number;
}

const VariationNamingModal: React.FC<VariationNamingModalProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultName,
  variationIndex,
  totalVariations,
}) => {
  const [name, setName] = useState(defaultName);

  const modalRef = useClickOutside(() => {
    if (isOpen) {
      onClose();
    }
  });

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim());
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center">
            <Edit3 className="h-5 w-5 text-fire-600 dark:text-fire-400 mr-2" />
            <div> {/* Keep this div */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">Name Your Variation</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {totalVariations === 1 ? 'Variation' : `Variation ${variationIndex + 1} of ${totalVariations}`}
              
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="variation-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Variation Name
            </label>
            <input
              id="variation-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text"
              placeholder="Enter a name for this variation..."
              autoFocus
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This will be used as the title for the new prompt
            </p>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-dark-border">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim()}
              className="bg-modular-primary-gradient text-white px-6 py-2 rounded-lg hover:shadow-modular-glow 
                focus:outline-none focus:ring-2 focus:ring-fire-500 focus:ring-offset-2 
                transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed 
                flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Variation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PromptVariationModal: React.FC<PromptVariationModalProps> = ({
  isOpen,
  onClose,
  originalPrompt,
  onSaveVariation,
}) => {
  const [variationCount, setVariationCount] = useState(3);
  const [variationInstruction, setVariationInstruction] = useState('');
  const [variations, setVariations] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showNamingModal, setShowNamingModal] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<{ variation: string; index: number } | null>(null);

  const modalRef = useClickOutside(() => {
    if (isOpen && !isGenerating && !showNamingModal) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  const handleGenerate = async () => {
    if (!variationInstruction.trim()) {
      setError('Please describe how you want the prompt to be varied.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setVariations([]);

    try {
      console.log('Generating variations with:', {
        originalPrompt: originalPrompt.body,
        count: variationCount,
        instruction: variationInstruction.trim(),
      });

      const newVariations = await openAIService.generateVariations({
        originalPrompt: originalPrompt.body,
        count: variationCount,
        instruction: variationInstruction.trim(),
      });

      console.log('Generated variations:', newVariations);
      setVariations(newVariations);
    } catch (err) {
      console.error('Variation generation error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate variations';
      
      // Show user-friendly error with setup guidance
      if (errorMessage.includes('API key not configured')) {
        setError('OpenAI API key not configured. Please set up VITE_OPENAI_API_KEY in your Netlify environment variables and redeploy the site.');
      } else if (errorMessage.includes('API key')) {
        setError('Invalid OpenAI API key. Please check that your API key is correct and starts with "sk-".');
      } else if (errorMessage.includes('rate limit')) {
        setError('OpenAI API rate limit exceeded. Please try again in a moment.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleSaveClick = (variation: string, index: number) => {
    const defaultName = variationCount === 1 
      ? `${originalPrompt.title} (Variation)` 
      : `${originalPrompt.title} (Variation ${index + 1})`;
    
    setSelectedVariation({ variation, index });
    setShowNamingModal(true);
  };

  const handleSaveWithName = (name: string) => {
    if (!selectedVariation) return;

    onSaveVariation({
      title: name,
      body: selectedVariation.variation,
      aiPlatform: originalPrompt.aiPlatform,
      isFavorite: false,
      isTemplate: originalPrompt.isTemplate,
      domain: originalPrompt.domain,
      output: originalPrompt.output,
      platforms: originalPrompt.platforms,
      webPlatforms: originalPrompt.webPlatforms,
    });
    
    // Show success feedback
    console.log('Variation saved successfully with name:', name);
    setSelectedVariation(null);
  };

  const getGenerateButtonText = () => {
    if (isGenerating) {
      return variationCount === 1 ? 'Generating variation...' : 'Generating variations...';
    }
    return variationCount === 1 ? 'Generate 1 Variation' : `Generate ${variationCount} Variations`;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
            <div className="flex items-center">
              <Wand2 className="h-6 w-6 text-fire-600 dark:text-fire-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">Generate Variations</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Create AI-powered variations of "{originalPrompt.title}"</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Original Prompt Display */}
            <div className="bg-gray-50 dark:bg-dark-card rounded-lg p-4 border border-gray-200 dark:border-dark-border">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Original Prompt:</h3>
              <p className="text-gray-900 dark:text-dark-text text-sm leading-relaxed">{originalPrompt.body}</p>
            </div>

            {/* Variation Instructions */}
            <div className="bg-modular-canvas-fog dark:bg-modular-shadow-quartz/20 border border-modular-stone-alloy dark:border-modular-graphite-smoke rounded-lg p-4">
              <label htmlFor="variationInstruction" className="block text-sm font-medium text-fire-700 dark:text-fire-300 mb-3">
                <Wand2 className="h-4 w-4 inline mr-2" />
                How would you like to vary this prompt?
              </label>
              <textarea
                id="variationInstruction"
                value={variationInstruction}
                onChange={(e) => setVariationInstruction(e.target.value)}
                placeholder="Describe how you want the prompt to be varied. For example:
• Make it more specific to e-commerce product descriptions
• Add a focus on sustainability and eco-friendliness
• Transform it for a younger audience (Gen Z)
• Make it suitable for social media captions
• Add technical specifications and measurements {/* Keep this text */}
• Convert it to a question-based format
• Make it more creative and artistic
• Adapt it for professional business use"
                className="w-full px-3 py-3 border border-fire-300 dark:border-fire-700 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                  bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text 
                  placeholder-gray-500 dark:placeholder-gray-400 min-h-[120px] resize-y"
                required
              />
              <div className="mt-2 flex items-start space-x-2"> {/* Keep this div */}
                <div className="flex-shrink-0 w-2 h-2 bg-fire-400 rounded-full mt-2"></div>
                <p className="text-xs text-fire-600 dark:text-fire-400 leading-relaxed">
                  <strong>Pro tip:</strong> Be specific about the changes you want. The more detailed your description, 
                  the better the AI can tailor the variations to your exact needs.
                </p>
              </div>
            </div>

            {/* Count Selection */}
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Number of variations:
              </label>
              <select
                value={variationCount}
                onChange={(e) => setVariationCount(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text"
              >
                <option value={1}>1 variation</option>
                <option value={2}>2 variations</option>
                <option value={3}>3 variations</option>
                <option value={4}>4 variations</option>
                <option value={5}>5 variations</option>
              </select>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate} // Keep this action
              disabled={isGenerating || !variationInstruction.trim()}
             className={`w-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed 
               flex items-center justify-center font-medium gap-x-1
               ${isGenerating 
                ? 'h-11 px-4 bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs rounded border border-gray-300 dark:border-gray-600' 
                : 'py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm shadow-sm hover:shadow-md rounded-lg'
               }`}
            >
                {isGenerating ? (
                  <div className="flex items-center">
                    <div className="w-[30px] h-[30px] flex items-center justify-center scale-[0.25] origin-center overflow-hidden">
                      <BoxLoader />
                    </div>
                    <span>{getGenerateButtonText()}</span>
                  </div>
                ) : (
                  <>
                   <Wand2 className="h-4 w-4 mr-2" />
                    {getGenerateButtonText()}
                  </>
                )}
            </button>

            {/* Error Display */}
            {error && ( // Keep this logic
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                <div className="mt-2 text-xs text-red-600 dark:text-red-500">
                  <p><strong>Troubleshooting tips:</strong></p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Make sure you have an OpenAI API key configured</li>
                    <li>Check your internet connection</li>
                    <li>Try with a shorter or simpler instruction</li>
                    <li>Refresh the page and try again</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Variations Display */}
            {variations.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text"> {/* Keep this text */}
                    Generated {variationCount === 1 ? 'Variation' : 'Variations'}
                  </h3>
                  <div className="text-xs text-fire-600 dark:text-fire-400 bg-fire-50 dark:bg-fire-900/20 
                    px-3 py-1 rounded-full border border-fire-200 dark:border-fire-800">
                    Based on: {variationInstruction.slice(0, 50)}{variationInstruction.length > 50 ? '...' : ''}
                  </div>
                </div>
                {variations.map((variation, index) => (
                  <div key={index} className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border p-4 hover:shadow-lg transition-shadow duration-300">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {variationCount === 1 ? 'Variation' : `Variation ${index + 1}`}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCopy(variation, index)}
                          className={`flex items-center px-3 py-1.5 text-sm rounded-lg transition-all duration-300 ${
                            copiedIndex === index // Keep this logic
                              ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                              : 'text-gray-600 dark:text-gray-300 hover:text-fire-600 dark:hover:text-fire-400 hover:bg-fire-50 dark:hover:bg-fire-900/20'
                          }`}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          {copiedIndex === index ? 'Copied!' : 'Copy'}
                        </button>
                        
                        <button
                          onClick={() => handleSaveClick(variation, index)}
                          className="flex items-center px-3 py-1.5 text-sm text-modular-accent-blue dark:text-modular-accent-blue 
                            hover:text-fire-700 dark:hover:text-fire-300 hover:bg-fire-50 
                            dark:hover:bg-fire-900/20 rounded-lg transition-all duration-300"
                        >
                          <Save className="h-4 w-4 mr-1" />
                          Save as New
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-900 dark:text-dark-text text-sm leading-relaxed whitespace-pre-wrap">
                      {variation}
                    </p>
                  </div>
                ))}
              </div>
            )
            }

            {/* Debug Info (only in development) */}
            {import.meta.env.DEV && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  <strong>Debug Info:</strong> API Key configured: {!!import.meta.env.VITE_OPENAI_API_KEY ? 'Yes' : 'No'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Variation Naming Modal */}
      <VariationNamingModal
        isOpen={showNamingModal}
        onClose={() => {
          setShowNamingModal(false);
          setSelectedVariation(null);
        }}
        onSave={handleSaveWithName}
        defaultName={selectedVariation ? (
          variationCount === 1 
            ? `${originalPrompt.title} (Variation)` 
            : `${originalPrompt.title} (Variation ${selectedVariation.index + 1})`
        ) : ''}
        variationIndex={selectedVariation?.index || 0}
        totalVariations={variationCount}
      />
    </>
  );
};